import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { istApiFehler } from "./api/client";
// NUR diese zwei Stildateien, in dieser Reihenfolge, und nur hier: index.css bindet
// tokens.css VOR base.css ein, app.css ergänzt danach die Schale.
import "./styles/index.css";
import "./styles/app.css";
import { App } from "./App";

/**
 * Die Vorgaben für JEDE Abfrage — an EINER Stelle, nicht je Haken (Muster lilasprojekte).
 *
 * `retry` wiederholt nur, was sich durch Wiederholen heilen lässt: ein 4xx bleibt ein
 * 4xx. Ein 401 wird ausdrücklich NICHT wiederholt — der Riegel in client.ts hat den
 * Anmeldestatus längst zurückgesetzt.
 *
 * `refetchOnWindowFocus: false`: die Live-Ereignisse (api/sse.ts) halten den Katalog
 * aktuell; ein Neuladen bei jedem Fensterwechsel holte nur dasselbe noch einmal.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (n: number, e: unknown) => n < 2 && !(istApiFehler(e) && e.status >= 400 && e.status < 500),
    },
    mutations: {
      // Eine Änderung wird NIE von selbst wiederholt: „Karte anlegen" zweimal ist
      // zwei Karten. Wer es noch einmal versuchen will, drückt noch einmal.
      retry: false,
    },
  },
});

// Nach einem Deploy tragen die Bausteine neue Namen, und ein Reiter, der die alte
// index.html noch offen hat, holt beim nächsten Bildschirmwechsel einen Baustein, den
// es nicht mehr gibt. Vite meldet das als `vite:preloadError`; die ehrliche Antwort ist
// EINMAL neu laden — der Merker verhindert eine Schleife.
const NEU_GELADEN = "kt.neu-geladen";
window.addEventListener("vite:preloadError", (ereignis) => {
  let schon = false;
  try {
    schon = sessionStorage.getItem(NEU_GELADEN) === "1";
    if (!schon) sessionStorage.setItem(NEU_GELADEN, "1");
  } catch {
    /* ohne Speicher: einmal versuchen ist immer noch besser als nie */
  }
  if (schon) return;
  ereignis.preventDefault();
  window.location.reload();
});
try {
  window.setTimeout(() => sessionStorage.removeItem(NEU_GELADEN), 10_000);
} catch {
  /* siehe oben */
}

const wurzel = document.getElementById("root");
if (!wurzel) throw new Error("Kein #root im Dokument — index.html wurde verändert.");

createRoot(wurzel).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>,
);
